// redux/slices/smsMessagesSlice.js
"use client";

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { resetStore } from "./resetStore";
import { getServerUrl, getAuthHeaders } from "../../Utils/serverSelector";

/* ============== HELPERS ============== */

const authJsonConfig = async () => {
  const headers = await getAuthHeaders();
  return {
    headers,
    withCredentials: true,
    validateStatus: () => true,
  };
};

const authJsonConfigWithContentType = async () => {
  const baseCfg = await authJsonConfig();
  return {
    ...baseCfg,
    headers: {
      ...baseCfg.headers,
      "Content-Type": "application/json",
    },
  };
};

/* ============== THUNKS ============== */

// GET /smsMessages/:uid
export const fetchSmsMessages = createAsyncThunk(
  "smsMessages/fetchSmsMessages",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const cfg = await authJsonConfig();
      const res = await axios.get(
        `${getServerUrl()}/smsMessages/${uid}`,
        cfg
      );

      if (res.status === 404) {
        // Нет сохранённых шаблонов — это ок, вернём null/пустое
        return null;
      }

      if (res.status !== 200) {
        throw new Error(
          res.data?.message ||
          res.data?.error ||
          `Failed to fetch SMS templates (HTTP ${res.status})`
        );
      }

      return res.data;
    } catch (e) {
      return rejectWithValue(
        e?.response?.data ||
        e?.message ||
        "Failed to fetch SMS templates"
      );
    }
  }
);

// POST /smsMessages
export const saveSmsMessages = createAsyncThunk(
  "smsMessages/saveSmsMessages",
  async (smsMessages, { rejectWithValue }) => {
    try {
      const cfg = await authJsonConfigWithContentType();
      const res = await axios.post(
        `${getServerUrl()}/smsMessages`,
        smsMessages,
        cfg
      );

      if (res.status !== 200 && res.status !== 201) {
        throw new Error(
          res.data?.message ||
          res.data?.error ||
          `Failed to save SMS templates (HTTP ${res.status})`
        );
      }

      return res.data;
    } catch (e) {
      return rejectWithValue(
        e?.response?.data ||
        e?.message ||
        "Failed to save SMS templates"
      );
    }
  }
);

/* ============== SLICE ============== */

const initialState = {
  messages: null,      // объект/массив шаблонов, как вернёт бэк
  status: "idle",      // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
};

const smsMessagesSlice = createSlice({
  name: "smsMessages",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetchSmsMessages
      .addCase(fetchSmsMessages.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchSmsMessages.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.messages = action.payload ?? null;
      })
      .addCase(fetchSmsMessages.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to fetch SMS templates";
      })

      // saveSmsMessages
      .addCase(saveSmsMessages.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(saveSmsMessages.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.messages = action.payload ?? null;
      })
      .addCase(saveSmsMessages.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to save SMS templates";
      })

      // reset
      .addCase(resetStore, () => initialState);
  },
});

export default smsMessagesSlice.reducer;
